/** @jsxImportSource @emotion/react */
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import {
  scrollStyle,
  gridStyle,
  cornerStyle,
  cornerMutedStyle,
  headStyle,
  headNameStyle,
  headAvgStyle,
  runInfoStyle,
  buildBadgeStyle,
  runDateStyle,
  runChangesStyle,
  cellStyle,
  runningCellStyle,
  cellDurationStyle,
  cellNoteStyle,
  rowClickableStyle,
} from './StageView.styles';

/** 스테이지 셀 상태. Jenkins Stage View의 색 구분에 대응. */
export type StageCellStatus =
  | 'success'
  | 'failed'
  | 'running'
  | 'aborted'
  | 'skipped'
  | 'empty';

/** 한 빌드의 한 스테이지 셀. */
export interface StageCell {
  status: StageCellStatus;
  /** 소요 표기. 미실행이면 생략. */
  durationLabel?: string;
  /** 셀 하단 부가 문구 (예: "(paused for 1h 24min)", "aborted"). */
  note?: string;
}

/** 리프(최하위) 스테이지 열. */
export interface StageLeaf {
  name: string;
  /** 하단 보조 라벨 (단계 번호 등). */
  no?: string;
}

/**
 * 스테이지 열 그룹.
 * leaves가 있으면 그 자식들을 2단 헤더로 묶어 표시,
 * 비어 있으면 그룹 자신이 단일 열(자식 행 없음).
 */
export interface StageColumnGroup {
  name: string;
  no?: string;
  leaves: StageLeaf[];
}

/** 한 빌드(행). */
export interface StageRun {
  /** 빌드 번호 (예: 4 → "#4"). */
  id: number;
  /** 실행 일시 표기 (예: "Aug 08 14:14"). */
  dateLabel: string;
  /** 변경 요약 (예: "No Changes"). */
  changes?: string;
  /** 빌드 전체 실패 여부 — 좌측 정보 셀 강조에 사용. */
  failed?: boolean;
  /** 열 순서에 맞춘 스테이지 셀 목록. */
  cells: StageCell[];
}

export interface StageViewProps {
  columns: StageColumnGroup[];
  runs: StageRun[];
  /** 평균 전체 소요 (예: "~1h 29min"). */
  avgRunLabel: string;
  /** 행(빌드) 클릭 시 호출 — 상세로 이동. 있으면 행 전체가 클릭 가능. */
  onOpenRun?: (id: number) => void;
}

/**
 * Jenkins Stage View 스타일 매트릭스.
 * 열 = 스테이지, 행 = 빌드. 각 셀은 스테이지 소요와 상태(색)를 보여준다.
 */
export function StageView({ columns, runs, avgRunLabel, onOpenRun }: StageViewProps) {
  // 리프 열: 자식이 있으면 자식들, 없으면 그룹 자신. 셀 정렬 기준.
  const leafColumns = columns.flatMap((g) =>
    g.leaves.length > 0 ? g.leaves : [{ name: g.name, no: g.no }]
  );
  // 자식을 가진 그룹이 하나라도 있으면 2단 헤더.
  const hasChildren = columns.some((g) => g.leaves.length > 0);

  return (
    <PanelCard title="스테이지 뷰">
      <div css={scrollStyle}>
        <table css={gridStyle}>
          <thead>
            <tr>
              <th css={cornerStyle} rowSpan={hasChildren ? 2 : 1}>
                스테이지 진행
                <span css={cornerMutedStyle}>({avgRunLabel})</span>
              </th>
              {columns.map((group, i) =>
                group.leaves.length > 0 ? (
                  <th key={i} css={headStyle} colSpan={group.leaves.length}>
                    <div css={headNameStyle}>{group.name}</div>
                    {/*{group.no && <div css={headAvgStyle}>{group.no}</div>}*/}
                  </th>
                ) : (
                  <th key={i} css={headStyle} rowSpan={hasChildren ? 2 : 1}>
                    <div css={headNameStyle}>{group.name}</div>
                    {/*{group.no && <div css={headAvgStyle}>{group.no}</div>}*/}
                  </th>
                )
              )}
            </tr>
            {hasChildren && (
              <tr>
                {columns.flatMap((group, i) =>
                  group.leaves.length > 0
                    ? group.leaves.map((leaf, j) => (
                        <th key={`${i}-${j}`} css={headStyle}>
                          <div css={headNameStyle}>{leaf.name}</div>
                          {/*{leaf.no && <div css={headAvgStyle}>{leaf.no}</div>}*/}
                        </th>
                      ))
                    : []
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.id}
                css={onOpenRun && rowClickableStyle}
                onClick={onOpenRun ? () => onOpenRun(run.id) : undefined}
              >
                <td css={runInfoStyle(run.failed ?? false)}>
                  <span css={buildBadgeStyle(run.failed ?? false)}>#{run.id}</span>
                  <div css={runDateStyle}>{run.dateLabel}</div>
                  {run.changes && <div css={runChangesStyle}>{run.changes}</div>}
                </td>
                {leafColumns.map((_, i) => {
                  const cell = run.cells[i] ?? { status: 'empty' as const };
                  return (
                    <td
                      key={i}
                      css={[
                        cellStyle(cell.status),
                        cell.status === 'running' && runningCellStyle,
                      ]}
                    >
                      {cell.durationLabel && (
                        <span css={cell.status === 'failed' ? cellDurationStyle : undefined}>
                          {cell.durationLabel}
                        </span>
                      )}
                      {cell.note && <span css={cellNoteStyle}>{cell.note}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PanelCard>
  );
}
